import {useDispatch,useSelector} from 'react-redux'
import {useEffect,useState,useMemo} from 'react'
import {setPoses,setLogViewer} from './slice'
import {
	frontOverheadShoulderAngle,
	projectKeypointsUntoAxis,
	countTotalHandsOffHips,
	calculateShoulderTilt,
	calculateThighLength,
	getThighValgusAngle,
	interpolateMidpoints,
	kneeForwardFlexion,
	sideShoulderAngle,
	calculateHipTilt,
	torsoSideAngle,
	sideKneeAngle,
	confidentSide,
	calfSideAngle,
	balancingSide,
	frontHipShift,
	hipPosition,
	poseHeight,
	smooth
} from './util'
import {capitalize} from '../../util/util'
import Table from 'react-bootstrap/Table'

const SquatTable = ({log,idx})=>{


	const startPose = log[0].keypoints
	const leftKneeStart = startPose[13]
	const rightKneeStart = startPose[14]
	const startKneeDistance = Math.abs(leftKneeStart.x - rightKneeStart.x)
	const height = poseHeight(startPose)
	const hipStartY = hipPosition(startPose).y
	const dispatch = useDispatch()
	
	const leftKnee = log[idx].keypoints[13]
	const rightKnee = log[idx].keypoints[14]

	const kneeDistance = Math.abs(leftKnee.x - rightKnee.x)

	//get idx where person turned sideways
	const sideIdx = log.findIndex(({keypoints})=>{
		const leftKnee = keypoints[13]
		const rightKnee = keypoints[14]
		const kneeDistance = Math.abs(leftKnee.x - rightKnee.x)
		return kneeDistance/startKneeDistance < 0.33
	})

	const side = idx >= sideIdx ? 'side' : 'front'

	const isFrontView = side === 'front'
	
	//how much absolute difference between torso and calf angle that is
	//considered "in the green"
	const trunkGreenTolerance = 10
	const hipShiftGreenTolerance = 2
	const torsoAngle = torsoSideAngle(log[idx].keypoints,side)
	const calfAngle = calfSideAngle(log[idx].keypoints,side)
	const diff = calfAngle - torsoAngle
	const depthRatio = Math.abs(hipStartY - hipPosition(log[idx].keypoints).y)/height
	const depthInches = depthRatio*69
	const depthPercent = depthRatio*100
	const kneeFwdFlexion = kneeForwardFlexion(log[idx].keypoints,side,height)
	const hipShift = frontHipShift(log[idx].keypoints,height)

	//so we need to add an offset for this. This offset is calculated
	//assuming a heal to medial distance of 1.75 inches. 
	//We create a unitless value 1.75/69 (ie percent of total height) 
	//then convert this to a value based on the estimated pose height
	const toeOffset = 1.75/69*height
	
	const leftAnkle = log[idx].keypoints[15]
	const rightAnkle = log[idx].keypoints[16]

	const leftToeX = leftAnkle.x - toeOffset
	const rightToeX = rightAnkle.x + toeOffset

	const leftValgus = (leftToeX - leftKnee.x)/height*69
	const rightValgus = (rightKnee.x - rightToeX)/height*69

	const leftValgusAngle = getThighValgusAngle('left',log[idx].keypoints)
	const rightValgusAngle = getThighValgusAngle('right',log[idx].keypoints)

	const healthyKneeSymmetryThreshold = 2
	const kneeSymmetryDistance = Math.abs(leftValgus - rightValgus)
	const kneeSymmetryDistanceAngle = Math.abs(leftValgusAngle - rightValgusAngle)
	const healthyKneeSymmetryAngleThreshold = 12

	return (
		<Table className="ml-2" striped bordered size="sm">
			<thead>
				<th className="text-center" colSpan="100%">
					{capitalize(side)} View
				</th>
				<tr>
					<th width="200px">Measure</th>
					<th width="120px">Value</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Squat Depth (percent)</td>
					<td align="right" style={{fontFamily:'monospace'}}>
						{depthPercent.toFixed(1)}%
					</td>
				</tr>
				<tr>
					<td>Squat Depth (inches)</td>
					<td align="right" style={{fontFamily:'monospace'}}>
						{depthInches.toFixed(1)}"
					</td>
				</tr>
				{isFrontView&&
					<>
						<tr>
							<td>Hip Shift</td>
							<td align="right" style={{
								fontFamily:'monospace',
								color:Math.abs(hipShift)<hipShiftGreenTolerance?'green':'red'
							}}>
								{Math.abs(hipShift).toFixed(1)}"
								<br/>
								{Math.abs(hipShift)<hipShiftGreenTolerance&&' (healthy)'}
								{hipShift<0-hipShiftGreenTolerance&&' (right)'}
								{hipShift>hipShiftGreenTolerance&&' (left)'}
							</td>
						</tr>
						<tr>
							<td>Left Thigh Valgus Angle</td>
							<td 
								style={{
									fontFamily:'monospace',
									color:leftValgusAngle<0?'red':'green'
								}}
								align="right" 
							>
								{leftValgusAngle.toFixed(1)}&#xb0;
								<br/>
								{leftValgusAngle<0?' (at risk)':' (healthy)'}
							</td>
						</tr>
						<tr>
							<td>Right Thigh Valgus Angle</td>
							<td 
								style={{
									fontFamily:'monospace',
									color:rightValgusAngle<0?'red':'green'
								}}
								align="right" 
							>
								{rightValgusAngle.toFixed(1)}&#xb0;
								<br/>
								{rightValgusAngle<0?' (at risk)':' (healthy)'}
							</td>
						</tr>
						<tr>
							<td>Knee Symmetry Difference (degrees)</td>
							<td
								style={{
									fontFamily:'monospace',
									color:kneeSymmetryDistanceAngle>healthyKneeSymmetryAngleThreshold?'red':'green'
								}}
								align="right"
							>
								{kneeSymmetryDistanceAngle.toFixed(1)}&#xb0;
								<br/>
								{kneeSymmetryDistanceAngle>healthyKneeSymmetryAngleThreshold?' (at risk)':' (healthy)'}
							</td>
						</tr>
						<tr>
							<td>Left Knee Medial Distance</td>
							<td 
								style={{
									fontFamily:'monospace',
									color:leftValgus>0?'red':'green'
								}}
								align="right" 
							>
								{leftValgus.toFixed(1)}"
								<br/>
								{leftValgus>0?' (at risk)':' (healthy)'}
							</td>
						</tr>
						<tr>
							<td>Right Knee Medial Distance</td>
							<td
								style={{
									fontFamily:'monospace',
									color:rightValgus>0?'red':'green'
								}}
								align="right"
							>
								{rightValgus.toFixed(1)}"
								<br/>
								{rightValgus>0?' (at risk)':' (healthy)'}
							</td>
						</tr>
						<tr>
							<td>Knee Symmetry Difference (inches)</td>
							<td
								style={{
									fontFamily:'monospace',
									color:kneeSymmetryDistance>healthyKneeSymmetryThreshold?'red':'green'
								}}
								align="right"
							>
								{kneeSymmetryDistance.toFixed(1)}"
								<br/>
								{kneeSymmetryDistance>healthyKneeSymmetryThreshold?' (at risk)':' (healthy)'}
							</td>
						</tr>
					</>
				}
				{!isFrontView&&
					<>
						<tr>
							<td>Knee Fwd. Flexion</td>
							<td align="right" style={{fontFamily:'monospace'}}>{kneeFwdFlexion.toFixed(1)}"</td>
						</tr>
						<tr>
							<td>Trunk Angle</td>
							<td align="right" style={{fontFamily:'monospace'}}>{torsoAngle.toFixed(1)}&#xb0;</td>
						</tr>
						<tr>
							<td>Leg Angle</td>
							<td align="right" style={{fontFamily:'monospace'}}>{calfAngle.toFixed(1)}&#xb0;</td>
						</tr>
						<tr>
							<td>Trunk-Shin Angle Diff.</td>
							<td align="right" style={{
								fontFamily:'monospace',
								color:diff>trunkGreenTolerance?'red':'green'
							}}>
								{diff.toFixed(1)}&#xb0;
								<br/>
								{diff>trunkGreenTolerance ? ' (collapsed)': ' (healthy)'}
							</td>
						</tr>
					</>
				}
			</tbody>
		</Table>
	)
}

const SingleLegSquatTable = ({log,results,idx})=>{
	const startPose = log[0].keypoints
	
	const thighLength = (
		calculateThighLength(startPose,'left') +
		calculateThighLength(startPose,'right')
	)/2

	const height = poseHeight(startPose)

	const keypoints = log[idx].keypoints

	//get indexes for when left/right side started
	const leftIdx = log.findIndex(({keypoints})=>balancingSide(keypoints)==='left')
	const rightIdx = log.findIndex(({keypoints})=>balancingSide(keypoints)==='right')

	const side = (
		((leftIdx>rightIdx&&idx>=leftIdx)&&'left') ||
		((leftIdx>rightIdx&&idx<leftIdx)&&'right') ||
		((rightIdx>leftIdx&&idx>=rightIdx)&&'right') ||
		((rightIdx>leftIdx&&idx<rightIdx)&&'left')
	)

	const bentThighLength = calculateThighLength(
		projectKeypointsUntoAxis(keypoints,'y'),
		side
	)

	const kneeAngle = Math.acos(
		bentThighLength/thighLength
	)*180/Math.PI || 0

	const valgusAngle = getThighValgusAngle(side,keypoints)

	const hipAngle = calculateHipTilt(keypoints)*( side === 'left' ? -1 : 1 )

	const hipDrop = Math.sin(hipAngle/180*Math.PI)*18

	const leftKnee = keypoints[13]
	const rightKnee = keypoints[14]

	const leftAnkle = keypoints[15]
	const rightAnkle = keypoints[16]

	//valgus is measured from the medial part of the big toe 
	//so we need to add an offset for this. This offset is calculated
	//assuming a heal to medial distance of 1.75 inches. 
	//We create a unitless value 1.75/69 (ie percent of total height) 
	//then convert this to a value based on the estimated pose height
	const toeOffset = 1.75/69*height

	const valgusDistance = (
		side === 'left' 
		? leftKnee.x - leftAnkle.x - toeOffset
		: rightAnkle.x - rightKnee.x - toeOffset
	)

	const kneeDeviation = valgusDistance/height*69

	const sideStartIdx = log.findIndex( ({t}) => {
		const startTime = results[side].reps.started[0]
		return t === startTime
	})

	const sideEndIdx = log.findIndex( ({t}) => {
		const endTime = results[side].reps.ended.at(-1)
		return t === endTime
	})

	const totalHandsOffHips = countTotalHandsOffHips(height,log.slice(sideStartIdx,Math.min(idx,sideEndIdx)))

	return (
		<Table className="ml-2" striped bordered size="sm">
			<thead>
				<th className="text-center" colSpan="100%">
					{capitalize(side)} Side
				</th>
				<tr>
					<th width="200px">Measure</th>
					<th width="120px">Value</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Knee Flexion Angle</td>
					<td align="right" style={{fontFamily:'monospace'}}>
						{kneeAngle.toFixed(1)}{String.fromCharCode(176)}
					</td>
				</tr>
				<tr>
					<td>Valgus Angle</td>
					<td align="right" style={{fontFamily:'monospace'}}>
						{valgusAngle.toFixed(1)}{String.fromCharCode(176)}
					</td>
				</tr>
				<tr>
					<td>Hip Drop</td>
					<td align="right" style={{fontFamily:'monospace'}}>
						{hipDrop.toFixed(1)}"
					</td>
				</tr>
				<tr>
					<td>Knee Deviation</td>
					<td 
						className={kneeDeviation<=0?'text-success':'text-danger'}
						align="right" 
						style={{fontFamily:'monospace'}}
					>
						{Math.abs(kneeDeviation).toFixed(1)}" ({kneeDeviation<=0?'varus':'valgus'})
					</td>
				</tr>
				<tr>
					<td>Hands Off Hips Count</td>
					<td 
						className={
							totalHandsOffHips <= 0
							? 'text-success'
							: 'text-danger'
						}
						align="right" 
						style={{fontFamily:'monospace'}}
					>
						{totalHandsOffHips}
					</td>
				</tr>
			</tbody>
		</Table>
	)
}

const BalanceTable = ({log,results,idx})=>{
	const keypoints = log[idx].keypoints
	const side = balancingSide(keypoints)
	const shoulderTilt = calculateShoulderTilt(keypoints)
	return (
		<Table className="ml-2" striped bordered size="sm">
			<thead>
				<th className="text-center" colSpan="100%">
					{capitalize(side)} Side
				</th>
				<tr>
					<th width="200px">Measure</th>
					<th width="120px">Value</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Shoulder Tilt</td>
					<td align="right" style={{fontFamily:'monospace'}}>
						{shoulderTilt.toFixed(1)}{String.fromCharCode(176)}
					</td>
				</tr>
			</tbody>
		</Table>
	)
}

const KneeFlexionTable = ({log,results,idx})=>{
	const keypoints = log[idx].keypoints
	const side = confidentSide(keypoints)
	const angle = 180 - sideKneeAngle(side,keypoints)
	return (
		<Table className="ml-2" striped bordered size="sm">
			<thead>
				<th className="text-center" colSpan="100%">
					{capitalize(side)} Side
				</th>
				<tr>
					<th width="200px">Measure</th>
					<th width="120px">Value</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Knee Angle</td>
					<td align="right" style={{fontFamily:'monospace'}}>
						{angle.toFixed(1)}{String.fromCharCode(176)}
					</td>
				</tr>
			</tbody>
		</Table>
	)
}

const ArmMobilityTable = ({log,results,idx}) => {
	const {t,keypoints} = log[idx]
	const side = (()=>{
		if (t < results.left.flexionStarted) return 'front' 
		if (t < results.right.flexionStarted) return 'left'
		return 'right'
	})()
	const displayData = (()=>{
		if ( side === 'front' ) {
			const leftSagittal = frontOverheadShoulderAngle('left',keypoints)
			const rightSagittal = frontOverheadShoulderAngle('right',keypoints)
			const diff = leftSagittal - rightSagittal
			return {
				heading:'Overhead Sagittal Angle',
				rows:[
					{
						measure:'Left',
						value:leftSagittal
					},{
						measure:'Right',
						value:rightSagittal
					}
				]
			}
		}
		if (side==='left') {
			const trunkAngle = torsoSideAngle(keypoints,'left') - 90
			if ( t < results.left.extensionStarted ) {
				return {
					heading:'Left Side',
					rows:[
						{
							measure:'Overhead Flexion Angle',
							value:sideShoulderAngle('left',keypoints)
						},{
							measure:'Trunk Angle',
							value:trunkAngle
						}
					]
				}
			} else {
				return {
					heading:'Left Side',
					rows:[
						{
							measure:'Posterior Extension Angle',
							value:sideShoulderAngle('left',keypoints)
						},{
							measure:'Trunk Angle',
							value:trunkAngle
						}
					]
				}
			}
			
		}
		if (side==='right') {
			const trunkAngle = torsoSideAngle(keypoints,'right') - 90
			if (t < results.right.extensionStarted) {
				return {
					heading:'Right Side',
					rows:[
						{
							measure:'Overhead Flexion Angle',
							value:sideShoulderAngle('right',keypoints)
						},{
							measure:'Trunk Angle',
							value:trunkAngle
						}
					]
				}
			}else {
				return {
					heading:'Right Side',
					rows:[
						{
							measure:'Posterior Extension Angle',
							value:sideShoulderAngle('left',keypoints)
						},{
							measure:'Trunk Angle',
							value:trunkAngle
						}
					]
				}
			}
		}
		return {
			heading:'',
			rows:[]
		}
	})()
	return (
		<Table className="ml-2" striped bordered size="sm">
			<thead>
				<th className="text-center" colSpan="100%">
					{capitalize(displayData.heading)}
				</th>
				<tr>
					<th width="200px">Measure</th>
					<th width="120px">Value</th>
				</tr>
			</thead>
			<tbody>
				{displayData.rows.map(({measure,value})=>(
					<tr>
						<td width="200px">{measure}</td>
						<td width="120px">{value.toFixed(1)}{String.fromCharCode(176)}</td>
					</tr>
				))}
			</tbody>
		</Table>
	)
}

const edges = [
	[4,2],
	[2,0],
	[0,1],
	[1,3],
	[10,8],
	[8,6],
	[6,5],
	[6,12],
	[12,14],
	[12,11],
	[14,16],
	[5,7],
	[5,11],
	[11,13],
	[13,15],
	[7,9]
]

const Poses = ({poses,threshold,scale,pointColor="#fff",lineColor="#fff",size})=>{
	//reflect all points accross the x-axis to mirror 
	poses = poses.map((pose)=>(
		{
			...pose,
			keypoints:pose.keypoints.map(kpt=>(
				{
					...kpt,
					x:size-kpt.x
				}
			))
		}
	))
	if(poses.length === 0) return null
	return ([
		poses.map(
			p => edges.map((e,ei)=>(
					(p.keypoints[e[0]].score>threshold&&p.keypoints[e[1]].score>threshold)&&
					<line 
						key={ei} 

						x1={p.keypoints[e[0]].x*scale} 
						y1={p.keypoints[e[0]].y*scale}

						x2={p.keypoints[e[1]].x*scale} 
						y2={p.keypoints[e[1]].y*scale}

						stroke={lineColor}
						strokeWidth={2}
					/>
				)//end edges map
			)//end p => 
		),//end poses map
		poses.map(
			p => p.keypoints.map(
				(kpt,kpti) => (
					kpt.score>threshold&&
						<circle
							fill={pointColor}
							cx={kpt.x*scale}
							cy={kpt.y*scale}
							key={kpti}
							r={2}
						/>
				)//end keypoints map
			)//end pi =>
		),//end poses map
	])
}

const KeypointLogViewer = ({id,demo,results,size=320})=>{
	const dispatch = useDispatch()

	useEffect(()=>{
		dispatch(
			setLogViewer({id,update:{idx:0,t:0}})
		)
	},[])

	//standardize keypoint log data to fit into the specified size
	const maxVal = results.keypointLog.reduce(
		(currentMaxVal,{keypoints})=>(
			keypoints.reduce(
				(currentMaxVal,{x,y})=>(
					Math.max(currentMaxVal,x,y)
				),
				currentMaxVal
			)
			
		),
		0
	)

	const numInterpolationPasses = 2
	const log = interpolateMidpoints(
		results.keypointLog.map(({t,keypoints})=>({
			keypoints:keypoints.map(kpt=>({
				...kpt,
				x:kpt.x/maxVal*size,
				y:kpt.y/maxVal*size,
			})),
			t
		})),
		numInterpolationPasses
	)
	
	const {idx} = useSelector(
		state => state.ai.logViewers[id] || {idx:0,demo:''}
	)

	const setIdx = (idx)=>{
		dispatch(
			setLogViewer({id,update:{idx}})
		)
	}

	const DataTable = ()=>{
		switch (demo) {
			case "squat": return (
				<SquatTable 
					log={log} 
					idx={idx} 
				/>
			)
			case "single-leg-squat": return (
				<SingleLegSquatTable 
					log={log} 
					results={results} 
					idx={idx}
				/>
			)
			case "balance": return (
				<BalanceTable 
					log={log} 
					results={results} 
					idx={idx}
				/>
			)
			case "knee-flexion": return (
				<KneeFlexionTable 
					log={log} 
					results={results} 
					idx={idx}
				/>
			)
			case "arm-overhead-movement": return (
				<ArmMobilityTable 
					log={log} 
					results={results} 
					idx={idx}
				/>
			)
			default : return `no data table for ${demo}`
		}
	}

	return (
		<div>
			<div className="d-flex flex-column align-items-center">
				<input style={{width:'500px'}} onChange={e=>{
					const idx = parseInt(e.target.value)
					setIdx(idx)
				}} type="range" value={idx} min={0} max={log.length - 1}/>
				<small>Drag the blue slider to view data at different times</small>
			</div>
			<div className="d-flex justify-content-center align-items-start my-3">
				<div className="d-flex flex-column align-items-center">
					<svg style={{background:'#00041c',width:`${size}px`,height:`${size}px`,borderRadius:'7px'}}>
						<Poses 
							pointColor="#e5e7f5"
							lineColor="#00ff8c"
							threshold={0.2} 
							poses={[log[idx]]}
							scale={1}
							size={size}
						/>
					</svg>
				</div>
				<div>
					<DataTable/>
				</div>
			</div>
		</div>
	)
}

export default KeypointLogViewer